import { useEffect, useState } from "react";
import { Star, CheckCircle2, Loader2 } from "lucide-react";
import { api, shopApi, formatApiError } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";

export default function ReviewsSection({ productId }) {
  const { user } = useCustomer();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ rating: 5, title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${productId}/reviews`).then(({ data }) => setData(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  const submit = async () => {
    if (!form.body.trim()) return toast.error("Please write a few words");
    setBusy(true);
    try {
      await shopApi.post("/reviews", { product_id: productId, ...form });
      toast.success("Thank you! Your review is pending approval.");
      setForm({ rating: 5, title: "", body: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not submit"); }
    setBusy(false);
  };

  return (
    <section className="mt-20" data-testid="reviews-section">
      <div className="flex items-center gap-3">
        <h2 className="font-serif text-2xl text-stone-900">Reviews</h2>
        {data?.count > 0 && (
          <span className="flex items-center gap-1 text-amber-500">
            <Star size={16} fill="currentColor" /><span className="text-sm text-stone-700">{data.average} · {data.count} review(s)</span>
          </span>
        )}
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-4">
          {!data ? <Loader2 className="animate-spin text-amber-500" />
            : data.reviews.length === 0 ? <p className="text-stone-500">No reviews yet — be the first to share your PRIYARAA story.</p>
              : data.reviews.map((r) => (
                <div key={r.id} className="rounded-xl border border-[#EAE2D3] bg-white p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-500 text-sm">{"★".repeat(r.rating)}<span className="text-stone-300">{"★".repeat(5 - r.rating)}</span></span>
                    {r.verified && <span className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] text-green-700"><CheckCircle2 size={11} /> Verified Purchase</span>}
                  </div>
                  {r.title && <p className="mt-1 font-medium text-stone-900">{r.title}</p>}
                  <p className="mt-1 text-sm text-stone-600">{r.body}</p>
                  <p className="mt-2 text-xs text-stone-400">{r.customer_name}</p>
                </div>
              ))}
        </div>

        <div className="rounded-xl border border-[#EAE2D3] bg-[#F3EDE2] p-5">
          <h3 className="font-serif text-lg text-stone-900">Write a review</h3>
          {user ? (
            <div className="mt-3 space-y-3">
              <div className="flex gap-1" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`review-star-${n}`} onClick={() => setForm((f) => ({ ...f, rating: n }))}>
                    <Star size={22} className={n <= form.rating ? "text-amber-500" : "text-stone-300"} fill={n <= form.rating ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
              <input data-testid="review-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title (optional)" className="w-full rounded-lg border border-[#E7DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-amber-500" />
              <textarea data-testid="review-body" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder="Share your experience…" rows={4} className="w-full rounded-lg border border-[#E7DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-amber-500" />
              <button data-testid="review-submit" onClick={submit} disabled={busy} className="flex items-center gap-2 rounded-full gold-gradient px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-stone-900 disabled:opacity-60">
                {busy && <Loader2 size={14} className="animate-spin" />} Submit Review
              </button>
            </div>
          ) : (
            <p className="mt-3 text-sm text-stone-500">Please <a href="/account" className="text-amber-700 underline">sign in</a> to write a review.</p>
          )}
        </div>
      </div>
    </section>
  );
}
